import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { FilterField } from "../../components/General/GenericFilter";
import { useAllCaptures, useReferenceData } from "../../hooks";
import { EquipmentResponse } from "../../types/model/warehouse/equipment";

export const STATUS_OPTIONS = ["available", "unavailable", "damaged"] as const;

export interface FilterOptions {
  equipmentType?: string;
  location?: string;
  captureId?: number;
  equipmentModel?: string;
  serialNumber?: string;
  equipmentBrandId?: number;
  status?: "available" | "unavailable";
}

const EMPTY_FILTERS: FilterOptions = {
  equipmentType: undefined,
  location: undefined,
  captureId: undefined,
  equipmentModel: undefined,
  serialNumber: undefined,
  equipmentBrandId: undefined,
  status: undefined,
};

const TEXT_FILTER_LABELS = {
  equipmentType: "warehouse.equipment.equipment-type",
  location: "warehouse.equipment.location",
  equipmentModel: "warehouse.equipment.equipment-model",
  serialNumber: "warehouse.equipment.serial-number",
} as const;

const includes = (value: string | null | undefined, search?: string) =>
  !search || value?.toLowerCase().includes(search.toLowerCase());

export const useEquipmentFilters = (equipmentData: EquipmentResponse[], onApply?: () => void) => {
  const { t } = useTranslation();
  const { data: brands = [] } = useReferenceData("construction", "brands");
  const captures = useAllCaptures();

  const [filters, setFilters] = useState<FilterOptions>(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState<FilterOptions>(EMPTY_FILTERS);

  const filterFields: FilterField[] = useMemo(
    () => [
      {
        name: "captureId",
        label: t("captures.label"),
        type: "autocomplete",
        options: captures.map((c) => ({ value: c.id, label: c.name })),
      },
      { name: "location", label: t(TEXT_FILTER_LABELS.location), type: "text" },
      { name: "equipmentType", label: t(TEXT_FILTER_LABELS.equipmentType), type: "text" },
      {
        name: "equipmentBrandId",
        label: t("warehouse.equipment.equipment-brand-id"),
        type: "autocomplete",
        options: brands.map((b) => ({ value: b.id, label: b.name })),
      },
      { name: "equipmentModel", label: t(TEXT_FILTER_LABELS.equipmentModel), type: "text" },
      { name: "serialNumber", label: t(TEXT_FILTER_LABELS.serialNumber), type: "text" },
      {
        name: "status",
        label: t("warehouse.equipment.status"),
        type: "autocomplete",
        options: STATUS_OPTIONS.map((s) => ({ value: s, label: t(`warehouse.equipment.statuses.${s}`) })),
      },
    ],
    [captures, brands, t],
  );

  const activeFilters = useMemo(() => {
    const list: { key: keyof FilterOptions; label: string; value: string }[] = [];

    if (filters.captureId) {
      const capture = captures.find((c) => c.id === filters.captureId);
      list.push({
        key: "captureId",
        label: t("captures.label"),
        value: capture?.name ?? filters.captureId.toString(),
      });
    }

    if (filters.equipmentBrandId) {
      const brand = brands.find((b) => b.id === filters.equipmentBrandId);
      list.push({
        key: "equipmentBrandId",
        label: t("warehouse.equipment.equipment-brand-id"),
        value: brand?.name ?? `ID: ${filters.equipmentBrandId}`,
      });
    }

    if (filters.status) {
      list.push({
        key: "status",
        label: t("warehouse.equipment.status"),
        value: t(`warehouse.equipment.statuses.${filters.status}`),
      });
    }

    (Object.keys(TEXT_FILTER_LABELS) as (keyof typeof TEXT_FILTER_LABELS)[]).forEach((key) => {
      const value = filters[key];
      if (value) list.push({ key, label: t(TEXT_FILTER_LABELS[key]), value });
    });

    return list;
  }, [filters, captures, brands, t]);

  const filteredData = useMemo(
    () =>
      equipmentData.filter(
        (item) =>
          (!filters.captureId || item.captureId === filters.captureId) &&
          (!filters.equipmentBrandId || item.equipmentBrandId === filters.equipmentBrandId) &&
          (!filters.status || item.status === filters.status) &&
          includes(item.equipmentType, filters.equipmentType) &&
          includes(item.location, filters.location) &&
          includes(item.equipmentModel, filters.equipmentModel) &&
          includes(item.serialNumber, filters.serialNumber),
      ),
    [equipmentData, filters],
  );

  const applyFilters = (newFilters: FilterOptions) => {
    setFilters(newFilters);
    setDraftFilters(newFilters);
    onApply?.();
  };

  const clearFilters = () => applyFilters(EMPTY_FILTERS);

  const removeFilter = (key: keyof FilterOptions) => applyFilters({ ...filters, [key]: undefined });

  return {
    draftFilters,
    setDraftFilters,
    filterFields,
    activeFilters,
    filteredData,
    applyFilters,
    clearFilters,
    removeFilter,
  };
};